import classNames from 'classnames';
import Head from 'next/head';

import Footer from './Footer';
import Header from './Header';
import { getHeroImage, getPostFeatureImage } from './helpers';
import { useStoreQueryParams } from './useStoreQueryParams';
import { GetLayoutQuery, GetPageQuery, GetPostQuery } from '../__generated__/graphql';
import BreadCrumbs from '../components/BreadCrumbs';
import { useBreadCrumbMenuItems } from '../components/BreadCrumbs/helpers';
import { AcfFragment } from '../content-blocks/types';
import { mapSeoDataToTags } from '../services/seo/mapSeoDataToTags';

import style from './layout.module.css';

type RenderAcfBlocks = (props: {
  acfFragments: AcfFragment[];
}) => React.ReactNode;

interface BaseProps {
  children: React.ReactNode | RenderAcfBlocks;
  containerClassName?: string;
  isLoading: boolean;
  layoutData: GetLayoutQuery;
  withBreadcrumbs?: boolean;
}

interface PropsWithPageData extends BaseProps {
  pageData: GetPageQuery;
  postData?: never;
}
interface PropsWithPostData extends BaseProps {
  pageData?: never;
  postData: GetPostQuery;
}

type Props = PropsWithPageData | PropsWithPostData;

const Layout = ({
  children,
  containerClassName,
  isLoading,
  layoutData,
  pageData,
  postData,
  withBreadcrumbs,
}: Props) => {
  const headerLogo = layoutData.headerLogo?.nodes[0];
  const menuButton = layoutData.menuButton?.nodes[0] ?? {};
  const menuItems = layoutData.headerMenuItems?.nodes ?? [];
  const footerMenu = layoutData.footerMenus?.nodes[0];
  const systemAlert = layoutData.systemAlert?.nodes[0] ?? {};

  // eslint-disable-next-line @stylistic/max-len
  const acfFragments = ((pageData?.page?.editorBlocks ?? postData?.post?.editorBlocks)?.filter(Boolean) ?? []) as AcfFragment[];

  useStoreQueryParams(layoutData.uTMConfiguration);

  const breadCrumbMenuItems = useBreadCrumbMenuItems({
    currentUri: pageData?.page?.uri ?? '',
    menuItems,
  });

  // Loading state for previews
  if (isLoading) {
    return <>Loading...</>;
  }

  const featureImage = pageData ? getHeroImage(acfFragments) : getPostFeatureImage(postData.post);

  const hasHeroBanner = acfFragments.some(acf => acf.__typename === 'AcfHerobanner');

  const shouldRenderBreadCrumbs = !!withBreadcrumbs && !!breadCrumbMenuItems.length;

  return (
    <>
      <Head>
        {mapSeoDataToTags({
          featureImage,
          seoData: pageData?.page?.seo ?? postData?.post?.seo,
        })}
      </Head>

      <Header
        headerLogo={headerLogo}
        menuButton={menuButton}
        menuItems={menuItems}
        systemAlert={systemAlert}
      />

      <main
        className={classNames(
          containerClassName,
          {
            [style.mainWithTopPadding]: hasHeroBanner && !shouldRenderBreadCrumbs,
          },
        )}
      >
        {shouldRenderBreadCrumbs && (
          <BreadCrumbs
            breadCrumbMenuItems={breadCrumbMenuItems}
          />
        )}

        {typeof children === 'function'
          ? children({ acfFragments })
          : children}
      </main>

      {footerMenu && <Footer menu={footerMenu} />}
    </>
  );
};

export default Layout;
